import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { Drawer, List } from '@material-ui/core';
import CssBaseline from '@material-ui/core/CssBaseline';
import IconButton from '@material-ui/core/IconButton';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import { logout, getRole } from '../../firebaseAuth';
import UserRoles from '../../enums/UserRoles';
import SidebarItem from './SidebarItem';
import store, { actions } from '../../store';
import { ReactComponent as Overview } from '../../assets/Icons/overview.svg';
import { ReactComponent as House } from '../../assets/Icons/house.svg';
import { ReactComponent as Services } from '../../assets/Icons/services.svg';
import { ReactComponent as Wallet } from '../../assets/Icons/wallet.svg';
import { ReactComponent as Notification } from '../../assets/Icons/notification.svg';
import { ReactComponent as Settings } from '../../assets/Icons/settings.svg';
import { ReactComponent as Stars } from '../../assets/Icons/stars.svg';
import { ReactComponent as Box } from '../../assets/Icons/box.svg';
import logOut from '../../assets/Icons/logout.svg';
import profile from '../../assets/Icons/profile.svg';
import icon from '../../assets/Icons/upkeep-icon.png';
import hamburger from '../../assets/Icons/hamburger-menu.svg';
import { colors } from '../../theme';
import AdminV2Redirect, { AdminV2RedirectType } from '../AdminV2Redirect';
import { useV2Store } from '../../store/v2/store';

const drawerWidth = 240;

const primaryColor = '#00AF9B';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    fontFamily: 'Poppins',
    backgroundColor: 'white',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingTop: theme.spacing(6),
      paddingBottom: theme.spacing(3),
    },
  },
  logoImage: {
    width: 30,
    [theme.breakpoints.up('sm')]: {
      width: 50,
    },
  },
  menuButton: {
    '& img': {
      width: 25,
      height: 25,
      [theme.breakpoints.up('sm')]: {
        width: 35,
        height: 35,
      },
    },
  },
  hide: {
    display: 'none',
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    borderRight: '1px solid rgba(0,0,0,.1)',
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: 50,
    [theme.breakpoints.up('sm')]: {
      width: 100,
    },
  },
  lightBackground: {
    background: '#FFFFFF',
  },
  darkBackground: {
    background: '#fff',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    minHeight: '100vh',
    overflow: 'auto',
  },
  noPaddingContent: {
    flexGrow: 1,
    minHeight: '100vh',
  },
  item: {
    '& span': {
      fontSize: '14px',
      fontFamily: 'Poppins',
    },
    '& img': {
      width: '22px',
      margin: '.5rem 2rem .5rem 1.25rem',
    },
    marginTop: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(6),
    },
  },
  line: {
    width: '85%',
  },
  category: {
    color: colors.grey['500'],
    fontSize: '12px',
    fontWeight: 600,
    padding: '1rem 2rem',
  },
  tab: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '8px',
  },
  selectedTab: {
    background: '#F2FBFA',
    borderRadius: '8px',
  },
  tabText: {
    paddingLeft: '1.5rem',
    color: colors.grey['500'],
  },
  selectedTabText: {
    paddingLeft: '1.5rem',
    color: primaryColor,
  },
  listItemWrapperOpen: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  listItem: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
}));

type SidebarProps = {
  children: React.ReactNode;
  hasContentSideBar?: boolean;
};

export default function Sidebar({ children, hasContentSideBar }: SidebarProps): JSX.Element {
  const classes = useStyles();
  const viewAsProvider = useSelector((state: store) => state.user.viewAsProvider);
  const provider = useSelector((state: store) => state.onboarding.provider);
  const [open, setOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const handleLogout = async () => {
    actions.abortOnboarding();
    actions.logout();
    await logout();
    history.push('/login');
  };
  const role = getRole();
  const isProvider = role === UserRoles.PROVIDER;
  const isAdmin = role === UserRoles.ADMIN;
  const [providerLocation, setProviderLocation] = useState<null | any>(null);

  const fetchLocation = useV2Store(state => state.fetchLocation);
  
  useEffect(() => {
    if (!provider?.location?.id) return;

    fetchLocation(provider.location.id).then(() => {
      const updatedLocation = useV2Store.getState().locations.find(
        loc => loc.id === provider.location.id
      );

      if (updatedLocation) {
        setProviderLocation(updatedLocation);
      }
    });
  }, [provider?.location?.id]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const { pathname } = location;

  return (
    <div className={classes.root}>
      <CssBaseline />
      <Drawer
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
          [classes.lightBackground]: isProvider,
          [classes.darkBackground]: !isProvider,
        })}
        classes={{
          paper: clsx({
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
            [classes.lightBackground]: isProvider,
            [classes.darkBackground]: !isProvider,
          }),
        }}>
        <div className={classes.logo}>
          <img src={icon} className={classes.logoImage} alt="logo" />
          {open && (
            <div
              style={{
                fontSize: '20px',
                color: '#4E4B66',
                fontWeight: 1000,
                letterSpacing: '.5px',
              }}>
              upkeep
            </div>
          )}
        </div>

        <div className={classes.toolbar}>
          {open ? (
            <IconButton onClick={handleDrawerClose}>
              <ChevronLeftIcon />
            </IconButton>
          ) : (
            <IconButton onClick={handleDrawerOpen} className={clsx(classes.menuButton, { [classes.hide]: open })}>
              <img src={hamburger} alt="hamburger" />
            </IconButton>
          )}
        </div>
        <List className={classes.item}>
          {(isProvider || viewAsProvider) && (
            <>
              <hr className={classes.line} />
              {open && <div className={classes.category}>Business tools</div>}
              <SidebarItem
                text="Overview"
                open={open}
                isSelected={pathname === '/provider/overview'}
                icon={<Overview stroke={pathname === '/provider/overview' ? primaryColor : colors.grey['500']} />}
                clickHandler={() => history.push({ pathname: '/provider/overview', state: { fromProvider: true } })}
              />
              {providerLocation?.settlementAccount?.id && (
                <AdminV2Redirect
                  type={AdminV2RedirectType.PAYOUTS}
                  id={providerLocation.settlementAccount.id}
                  inSidebar
                  open={open}
                />
              )}
              {providerLocation?.id && (
                <AdminV2Redirect
                  type={AdminV2RedirectType.LOCATION}
                  id={provider.location.id}
                  inSidebar
                  open={open}
                />
              )}
              <hr className={classes.line} />
              {open && <div className={classes.category}>Account</div>}
              <SidebarItem
                text="My Profile"
                open={open}
                isSelected={pathname === '/provider/profile'}
                icon={
                  !provider.logoUrl || provider.logoUrl === 'a.mock.path' ? (
                    <img src={profile} alt="profile" />
                  ) : (
                    <img src={provider.logoUrl} alt="logo" />
                  )
                }
                clickHandler={() => {
                  history.push('/provider/profile');
                }}
              />
            </>
          )}
          {!isProvider && !viewAsProvider && (
            <>
              <hr className={classes.line} />
              {open && <div className={classes.category}>Admin management</div>}
              <SidebarItem
                text="Overview"
                open={open}
                href="/overview"
                isSelected={pathname === '/overview'}
                icon={<Overview stroke={pathname === '/overview' ? primaryColor : colors.grey['500']} />}
              />
              <SidebarItem
                text="Providers"
                open={open}
                href="/providers"
                isSelected={pathname === '/providers'}
                icon={<House stroke={pathname === '/providers' ? primaryColor : colors.grey['500']} />}
                clickHandler={() => {
                  actions.setProvider({});
                  actions.setLocation({});
                }}
              />
              <SidebarItem
                text="Users"
                open={open}
                href="/users"
                isSelected={pathname === '/users'}
                icon={
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle
                      cx="11"
                      cy="10"
                      r="3"
                      stroke={pathname === '/users' ? primaryColor : colors.grey['500']}
                      strokeWidth="1.5"
                    />
                    <circle
                      cx="20.5"
                      cy="12.5"
                      r="2.5"
                      stroke={pathname === '/users' ? primaryColor : colors.grey['500']}
                      strokeWidth="1.5"
                    />
                    <path
                      d="M5 22C5 18.6863 7.68629 16 11 16V16C14.3137 16 17 18.6863 17 22V23C17 23.5523 16.5523 24 16 24H6C5.44772 24 5 23.5523 5 23V22Z"
                      stroke={pathname === '/users' ? primaryColor : colors.grey['500']}
                      strokeWidth="1.5"
                    />
                    <path
                      fill={pathname === '/users' ? primaryColor : colors.grey['500']}
                      d="M16.7572 20.3047L17.4767 20.0932L16.7572 20.3047ZM17.0202 19.2406L16.5454 18.66L17.0202 19.2406ZM16.25 22C16.25 21.4836 16.1757 20.9859 16.0376 20.5163L17.4767 20.0932C17.6547 20.6988 17.75 21.3389 17.75 22H16.25ZM16.25 23.6429V22H17.75V23.6429H16.25ZM17.3571 23.25H25.5V24.75H17.3571V23.25ZM25.25 23.5C25.25 20.8766 23.1234 18.75 20.5 18.75V17.25C23.9518 17.25 26.75 20.0482 26.75 23.5H25.25ZM20.5 18.75C19.3591 18.75 18.314 19.1513 17.495 19.8211L16.5454 18.66C17.6224 17.7791 19.0004 17.25 20.5 17.25V18.75ZM25.5 23.25C25.3619 23.25 25.25 23.3619 25.25 23.5H26.75C26.75 24.1904 26.1904 24.75 25.5 24.75V23.25ZM17.75 23.6429C17.75 23.4259 17.5741 23.25 17.3571 23.25L17.3571 24.75C16.7457 24.75 16.25 24.2543 16.25 23.6429H17.75ZM16.0376 20.5163C15.8696 19.9448 15.9317 19.1619 16.5454 18.66L17.495 19.8211C17.4877 19.8271 17.4728 19.8413 17.4631 19.8856C17.4522 19.935 17.4519 20.009 17.4767 20.0932L16.0376 20.5163Z"
                    />
                  </svg>
                }
              />
              <SidebarItem
                text="Services"
                open={open}
                href="/services"
                isSelected={pathname === '/services'}
                icon={<Services stroke={pathname === '/services' ? primaryColor : colors.grey['500']} />}
              />
              <SidebarItem
                text="Promocodes"
                open={open}
                href="/promocodes"
                isSelected={pathname === '/promocodes'}
                icon={<Wallet stroke={pathname === '/promocodes' ? primaryColor : colors.grey['500']} />}
              />
              <SidebarItem
                text="Credits"
                open={open}
                href="/credits"
                isSelected={pathname === '/credits'}
                icon={<Stars stroke={pathname === '/credits' ? primaryColor : colors.grey['500']} />}
              />
              <SidebarItem
                text="Pricing Tiers"
                open={open}
                href="/pricing-tiers"
                isSelected={pathname === '/pricing-tiers'}
                icon={
                  <svg width="21" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fill={pathname === '/pricing-tierss' ? primaryColor : colors.grey['500']}
                      d="M12.7707 1.02637L12.8225 0.278157L11.8059 0.207801L12.0409 1.19934L12.7707 1.02637ZM22.3313 8.21432L22.53 8.93751L23.316 8.72155L23.0355 7.95622L22.3313 8.21432ZM12.7927 1.11908L12.0629 1.29206L12.0629 1.29206L12.7927 1.11908ZM13.1311 1.7495L12.5837 2.26219L12.5837 2.26219L13.1311 1.7495ZM13.2833 1.91204L13.8307 1.39935L13.8307 1.39935L13.2833 1.91204ZM13.4993 2.96765L14.2041 3.22418L14.2041 3.22417L13.4993 2.96765ZM13.4617 3.07104L12.7569 2.81452L12.7569 2.81453L13.4617 3.07104ZM14.0111 4.24922L14.2676 3.54445L14.2676 3.54445L14.0111 4.24922ZM14.6106 5.2114L15.3563 5.29167L14.6106 5.2114ZM14.5336 5.92643L15.2793 6.00671V6.00671L14.5336 5.92643ZM15.0194 7.37907L15.5668 6.86638L15.5668 6.86638L15.0194 7.37907ZM15.3578 7.7404L15.9052 7.2277V7.2277L15.3578 7.7404ZM17.2096 8.33441L17.3567 9.06986L17.2096 8.33441ZM18.911 7.99427L19.058 8.72972H19.058L18.911 7.99427ZM19.6001 7.97764L19.4887 8.71932L19.4887 8.71932L19.6001 7.97764ZM21.5044 8.26362L21.6158 7.52193L21.6158 7.52193L21.5044 8.26362ZM5.89143 21.1492L5.47441 21.7725L5.87616 22.0413L6.28687 21.7864L5.89143 21.1492ZM6.72041 2.34726L7.23139 1.79827L6.8349 1.42923L6.35991 1.68958L6.72041 2.34726ZM7.39384 2.97407L7.90482 2.42508L7.90482 2.42507L7.39384 2.97407ZM7.79308 3.49159L7.13238 3.84651L7.13238 3.84651L7.79308 3.49159ZM8.22723 4.29976L7.56652 4.65469L7.58938 4.69723L7.61749 4.73649L8.22723 4.29976ZM9.035 5.42754L8.42526 5.86426L8.42527 5.86426L9.035 5.42754ZM9.51357 7.89163L8.80748 8.1445L8.80748 8.1445L9.51357 7.89163ZM8.77416 10.0104L8.37872 9.3731H8.37872L8.77416 10.0104ZM8.39275 10.2471L8.78819 10.8843L8.78819 10.8843L8.39275 10.2471ZM8.08463 10.4825L7.57365 9.93349L7.57365 9.93349L8.08463 10.4825ZM7.04541 11.4498L6.53442 10.9008H6.53442L7.04541 11.4498ZM6.15386 12.2796L6.66485 12.8286H6.66485L6.15386 12.2796ZM5.58495 14.2624L4.86062 14.4569L5.58495 14.2624ZM5.71473 14.7456L6.43906 14.551H6.43906L5.71473 14.7456ZM6.28364 15.6907L5.77266 16.2397H5.77266L6.28364 15.6907ZM6.68921 16.0682L7.20019 15.5192L7.20019 15.5192L6.68921 16.0682ZM7.20948 16.8579L6.50339 17.1108H6.50339L7.20948 16.8579ZM7.63632 18.0498L6.93023 18.3026L6.93023 18.3026L7.63632 18.0498ZM7.68231 18.1782L8.3884 17.9253L8.38839 17.9253L7.68231 18.1782ZM6.85392 20.5519L7.24936 21.1892L7.24936 21.1892L6.85392 20.5519ZM12.198 12.8198L11.6935 12.2649L11.6935 12.2649L12.198 12.8198ZM12.2882 12.7378L12.7927 13.2927L12.7927 13.2927L12.2882 12.7378ZM11.9574 15.2144L12.5622 14.7709V14.7709L11.9574 15.2144ZM12.2838 16.5573L13.0247 16.6737V16.6737L12.2838 16.5573ZM12.082 17.8417L11.3411 17.7253V17.7253L12.082 17.8417ZM12.1209 18.2386L11.4168 18.4968L11.4168 18.4968L12.1209 18.2386ZM13.1554 18.7055L13.4277 19.4043L13.1554 18.7055ZM14.2677 18.2721L14.54 18.9709L14.54 18.9709L14.2677 18.2721ZM15.1942 17.5237L15.8202 17.9368L15.8202 17.9368L15.1942 17.5237ZM15.8047 16.5987L15.1787 16.1856L15.8047 16.5987ZM16.0187 16.3284L15.473 15.8139L15.473 15.8139L16.0187 16.3284ZM16.8376 15.4599L17.3833 15.9744L16.8376 15.4599ZM16.6 13.2725L17.0225 12.6528L17.0059 12.6415L16.9887 12.6311L16.6 13.2725ZM15.8273 12.8042L15.4385 13.4456H15.4385L15.8273 12.8042ZM15.2339 12.5643L15.0677 13.2956H15.0677L15.2339 12.5643ZM14.15 12.3179L13.9325 13.0357L13.9579 13.0434L13.9838 13.0493L14.15 12.3179ZM12.7189 1.77458C16.8246 2.05873 20.2674 4.76262 21.6271 8.47241L23.0355 7.95622C21.4775 3.70538 17.5335 0.604203 12.8225 0.278157L12.7189 1.77458ZM12.0409 1.19934L12.0629 1.29206L13.5224 0.946109L13.5005 0.853394L12.0409 1.19934ZM12.0629 1.29206C12.149 1.6553 12.3285 1.98972 12.5837 2.26219L13.6785 1.23681C13.602 1.15516 13.5482 1.05495 13.5224 0.946105L12.0629 1.29206ZM12.5837 2.26219L12.7359 2.42473L13.8307 1.39935L13.6785 1.2368L12.5837 2.26219ZM12.7359 2.42473C12.8079 2.50154 12.8305 2.61226 12.7945 2.71114L14.2041 3.22417C14.4334 2.59414 14.2891 1.8887 13.8307 1.39935L12.7359 2.42473ZM12.7945 2.71112L12.7569 2.81452L14.1664 3.32757L14.2041 3.22418L12.7945 2.71112ZM12.7569 2.81453C12.4416 3.68082 12.8883 4.63869 13.7545 4.95399L14.2676 3.54445C14.1797 3.51249 14.1345 3.41538 14.1664 3.32756L12.7569 2.81453ZM13.7545 4.95399C13.8277 4.98062 13.8732 5.05372 13.8649 5.13112L15.3563 5.29167C15.4385 4.52817 14.9892 3.80709 14.2676 3.54445L13.7545 4.95399ZM13.8649 5.13112L13.7879 5.84616L15.2793 6.00671L15.3563 5.29167L13.8649 5.13112ZM13.7879 5.84616C13.7073 6.59509 13.9571 7.34198 14.472 7.89176L15.5668 6.86638C15.3504 6.63533 15.2454 6.32145 15.2793 6.00671L13.7879 5.84616ZM14.472 7.89176L14.8104 8.25309L15.9052 7.2277L15.5668 6.86638L14.472 7.89176ZM14.8104 8.25309C15.4607 8.94745 16.4238 9.25636 17.3567 9.06986L17.0626 7.59896C16.6386 7.68374 16.2008 7.54332 15.9052 7.2277L14.8104 8.25309ZM17.3567 9.06986L19.058 8.72972L18.764 7.25882L17.0626 7.59896L17.3567 9.06986ZM19.058 8.72972C19.1999 8.70135 19.3456 8.69783 19.4887 8.71932L19.7115 7.23596C19.3967 7.18868 19.0761 7.19642 18.764 7.25882L19.058 8.72972ZM19.4887 8.71932L21.393 9.0053L21.6158 7.52193L19.7115 7.23596L19.4887 8.71932ZM21.393 9.0053C21.7734 9.06243 22.1607 9.03899 22.53 8.93751L22.1326 7.49112C21.9647 7.53725 21.7887 7.5479 21.6158 7.52193L21.393 9.0053ZM6.30845 20.5258C3.55827 18.686 1.75 15.5539 1.75 11.9998H0.25C0.25 16.0756 2.32581 19.6662 5.47441 21.7725L6.30845 20.5258ZM1.75 11.9998C1.75 8.12268 3.90233 4.7472 7.0809 3.00494L6.35991 1.68958C2.72033 3.68453 0.25 7.55313 0.25 11.9998H1.75ZM7.90482 2.42507L7.23139 1.79827L6.20942 2.89625L6.88285 3.52306L7.90482 2.42507ZM8.45379 3.13666C8.31106 2.87097 8.12559 2.63056 7.90482 2.42508L6.88285 3.52306C6.9832 3.61646 7.0675 3.72574 7.13238 3.84651L8.45379 3.13666ZM8.88793 3.94484L8.45379 3.13666L7.13238 3.84651L7.56652 4.65469L8.88793 3.94484ZM9.64473 4.99081L8.83696 3.86304L7.61749 4.73649L8.42526 5.86426L9.64473 4.99081ZM10.159 6.59213C10.159 6.01774 9.97919 5.45777 9.64473 4.99081L8.42527 5.86426C8.57729 6.07651 8.65904 6.33104 8.65904 6.59213H10.159ZM10.159 7.28975V6.59213H8.65904V7.28975H10.159ZM10.2197 7.63876C10.1795 7.52677 10.159 7.4087 10.159 7.28975H8.65904C8.65904 7.58108 8.70926 7.87023 8.80748 8.1445L10.2197 7.63876ZM9.16961 10.6477C10.1851 10.0176 10.6226 8.76386 10.2197 7.63876L8.80748 8.1445C8.97201 8.6039 8.79336 9.11581 8.37872 9.3731L9.16961 10.6477ZM8.78819 10.8843L9.16961 10.6477L8.37872 9.3731L7.99731 9.60977L8.78819 10.8843ZM8.59561 11.0315C8.65489 10.9763 8.71939 10.927 8.78819 10.8843L7.99731 9.60977C7.84594 9.7037 7.70405 9.81212 7.57365 9.93349L8.59561 11.0315ZM7.55639 11.9988L8.59562 11.0315L7.57365 9.93349L6.53442 10.9008L7.55639 11.9988ZM6.66485 12.8286L7.55639 11.9988L6.53442 10.9008L5.64288 11.7306L6.66485 12.8286ZM6.30928 14.0678C6.1892 13.6208 6.32601 13.144 6.66485 12.8286L5.64288 11.7306C4.89744 12.4244 4.59646 13.4734 4.86062 14.4569L6.30928 14.0678ZM6.43906 14.551L6.30928 14.0678L4.86062 14.4569L4.99041 14.9401L6.43906 14.551ZM6.79463 15.1417C6.62307 14.9821 6.49986 14.7774 6.43906 14.551L4.99041 14.9401C5.12416 15.4381 5.39522 15.8884 5.77266 16.2397L6.79463 15.1417ZM7.20019 15.5192L6.79463 15.1417L5.77266 16.2397L6.17822 16.6172L7.20019 15.5192ZM7.91556 16.605C7.76733 16.1911 7.52202 15.8188 7.20019 15.5192L6.17822 16.6172C6.32451 16.7534 6.43601 16.9226 6.50339 17.1108L7.91556 16.605ZM8.3424 17.7969L7.91556 16.605L6.50339 17.1108L6.93023 18.3026L8.3424 17.7969ZM8.38839 17.9253L8.3424 17.7969L6.93023 18.3026L6.97622 18.4311L8.38839 17.9253ZM7.24936 21.1892C8.35087 20.5057 8.82547 19.1458 8.3884 17.9253L6.97622 18.4311C7.17489 18.9858 6.95917 19.604 6.45848 19.9146L7.24936 21.1892ZM6.28687 21.7864L7.24936 21.1892L6.45848 19.9146L5.49599 20.5119L6.28687 21.7864ZM12.7025 13.3748L12.7927 13.2927L11.7837 12.1828L11.6935 12.2649L12.7025 13.3748ZM12.5622 14.7709C12.2449 14.3382 12.3054 13.7357 12.7025 13.3748L11.6935 12.2649C10.7285 13.1421 10.5814 14.6063 11.3526 15.6579L12.5622 14.7709ZM13.0247 16.6737C13.1301 16.0032 12.9636 15.3183 12.5622 14.7709L11.3526 15.6579C11.5178 15.8832 11.5863 16.165 11.5429 16.4409L13.0247 16.6737ZM12.8229 17.9582L13.0247 16.6737L11.5429 16.4409L11.3411 17.7253L12.8229 17.9582ZM12.8251 17.9804C12.8225 17.9733 12.8217 17.9656 12.8229 17.9582L11.3411 17.7253C11.3003 17.9847 11.3264 18.2503 11.4168 18.4968L12.8251 17.9804ZM12.8831 18.0066C12.8599 18.0157 12.8337 18.0039 12.8251 17.9804L11.4168 18.4968C11.7146 19.3091 12.6215 19.7184 13.4277 19.4043L12.8831 18.0066ZM13.9955 17.5732L12.8831 18.0066L13.4277 19.4043L14.54 18.9709L13.9955 17.5732ZM14.5683 17.1105C14.4298 17.3203 14.2297 17.482 13.9955 17.5732L14.54 18.9709C15.0635 18.767 15.5107 18.4057 15.8202 17.9368L14.5683 17.1105ZM15.1787 16.1856L14.5683 17.1105L15.8202 17.9368L16.4306 17.0119L15.1787 16.1856ZM15.473 15.8139C15.3644 15.9291 15.266 16.0535 15.1787 16.1856L16.4306 17.0119C16.4703 16.9518 16.5151 16.8953 16.5644 16.8429L15.473 15.8139ZM16.2919 14.9454L15.473 15.8139L16.5644 16.8429L17.3833 15.9744L16.2919 14.9454ZM16.1775 13.8922C16.5313 14.1334 16.5857 14.6338 16.2919 14.9454L17.3833 15.9744C18.3098 14.9918 18.1384 13.4136 17.0225 12.6528L16.1775 13.8922ZM15.4385 13.4456L16.2113 13.9139L16.9887 12.6311L16.216 12.1628L15.4385 13.4456ZM15.0677 13.2956C15.1985 13.3254 15.3238 13.376 15.4385 13.4456L16.216 12.1628C15.9635 12.0097 15.688 11.8984 15.4001 11.8329L15.0677 13.2956ZM13.9838 13.0493L15.0677 13.2956L15.4001 11.8329L14.3162 11.5866L13.9838 13.0493ZM12.7927 13.2927C13.101 13.0125 13.5338 12.9149 13.9325 13.0357L14.3675 11.6002C13.4637 11.3263 12.4825 11.5476 11.7837 12.1828L12.7927 13.2927Z"
                    />
                    <circle
                      stroke={pathname === '/pricing-tiers' ? primaryColor : colors.grey['500']}
                      cx="12"
                      cy="12"
                      r="11"
                      strokeWidth="1.5"
                    />
                  </svg>
                }
              />
              <SidebarItem
                text="Provider Onboard"
                open={open}
                href="/provider/onboarding"
                isSelected={pathname === '/provider/onboarding'}
                icon={<Box stroke={pathname === '/provider/onboarding' ? primaryColor : colors.grey['500']} />}
              />
              {isAdmin && (
                <AdminV2Redirect
                  type={AdminV2RedirectType.RECONCILIATION}
                  inSidebar
                  open={open}
                />
              )}
              <hr className={classes.line} />
              {open && <div className={classes.category}>Account management</div>}
              <SidebarItem
                text="Notifications"
                open={open}
                href="/notifications"
                isSelected={pathname === '/notifications'}
                icon={<Notification stroke={pathname === '/notifications' ? primaryColor : colors.grey['500']} />}
              />
              <SidebarItem
                text="Settings"
                open={open}
                href="/settings"
                isSelected={pathname === '/settings'}
                icon={<Settings stroke={pathname === '/settings' ? primaryColor : colors.grey['500']} />}
              />
            </>
          )}
          <SidebarItem
            text="Log Out"
            open={open}
            icon={<img src={logOut} alt="logout" />}
            clickHandler={handleLogout}
          />
        </List>
      </Drawer>
      <main
        className={clsx(hasContentSideBar ? classes.noPaddingContent : classes.content, {
          [classes.darkBackground]: isProvider,
          [classes.lightBackground]: !isProvider,
        })}>
        {children}
      </main>
    </div>
  );
}
